import { useTranslation } from 'react-i18next';
import { ConfirmModal, ReasonModal } from '../../../ui/index.js';
import type { RussiaTransitionAction } from '../role.js';

/**
 * @aiPurpose confirm-modal for RF drawer transitions with side-effects.
 *   Keeps i18n key resolution off a dynamic template string so the
 *   strict-literal i18n type narrowing stays exhaustive — a switch
 *   over `action.key` makes TypeScript verify that every confirm key
 *   maps to a real i18n entry; a `${dynamic}` template would resolve
 *   to the union of all `actions.*` keys and trip the strict-literal
 *   resource-bundle check.
 */
export function RussiaTransitionConfirmDialog({
  action,
  fire,
  onClose,
}: {
  action: RussiaTransitionAction;
  fire: (a: RussiaTransitionAction) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('russiaRequests');
  let title: string;
  let text: string;
  if (action.key === 'close') {
    title = t('drawer.confirm.close.title');
    text = t('drawer.confirm.close.text');
  } else if (action.key === 'archive') {
    title = t('drawer.confirm.archive.title');
    text = t('drawer.confirm.archive.text');
  } else if (action.key === 'success') {
    title = t('drawer.confirm.success.title');
    text = t('drawer.confirm.success.text');
  } else if (action.key === 'refuse') {
    title = t('drawer.confirm.refuse.title');
    text = t('drawer.confirm.refuse.text');
  } else {
    // No other transition surfaces confirm today (see ACTION_META). Falling
    // back to a no-op render is safer than rendering a misleading modal.
    return null;
  }
  return (
    <ConfirmModal
      title={title}
      text={text}
      danger={action.kind === 'danger' || action.key === 'close'}
      onConfirm={() => fire(action)}
      onClose={onClose}
    />
  );
}

/**
 * @aiPurpose reason-modal for RF drawer transitions that require an
 *   explanatory string (`return` and, since #1544, `reopen`).
 */
export function RussiaTransitionReasonDialog({
  action,
  fire,
  onClose,
}: {
  action: RussiaTransitionAction;
  fire: (a: RussiaTransitionAction, reason: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('russiaRequests');
  let title: string;
  let text: string;
  if (action.key === 'return') {
    title = t('drawer.reason.return.title');
    text = t('drawer.reason.return.text');
  } else if (action.key === 'reopen') {
    title = t('drawer.reason.reopen.title');
    text = t('drawer.reason.reopen.text');
  } else {
    return null;
  }
  return (
    <ReasonModal
      title={title}
      text={text}
      danger={action.kind === 'danger'}
      onConfirm={(reason) => fire(action, reason)}
      onClose={onClose}
    />
  );
}
